import useSearch from './use-search';
import useFilters from './use-filters';
import useSRPs from './use-srps';

const toggleInArray = (source, value) => {
  const asArray = [...(Array.isArray(source) ? source : [source].filter(Boolean))];
  const result = asArray.includes(value)
    // if it's present, toggle it out
    ? asArray.filter((v) => v !== value)
    // value could be "", filter it out
    : asArray.concat(value).filter(Boolean);
  if (result.length === 0) {
    return undefined;
  }
  return result;
};

const emptyStringIsUndefined = (value) => {
  if (value === '') {
    return undefined;
  }
  return value;
};

const useField = (field) => {
  const {
    where: currentWhere,
    defaultWhere,
    setWhere,
    srp: currentSrp,
    setSrp,
  } = useSearch();
  const where = { ...defaultWhere, ...currentWhere };
  const srps = useSRPs();
  const filterValues = useFilters();

  const handleKeyValueToggle = ({ target: { value } }) => setWhere({
    ...where,
    [field]: value === where[field] ? undefined : emptyStringIsUndefined(value),
  });

  const handleKeyValueArrayToggle = ({ target: { value } }) => setWhere({
    ...where,
    [field]: toggleInArray(where[field], value),
  });

  if (field === 'srp') {
    return {
      value: srps.find(({ id }) => id === currentSrp.id)?.title || '',
      options: srps.map(({ title }) => ({ name: title })),
      handler: ({ target: { value } }) => {
        if (!value) {
          setWhere({
            ...where,
            predefined: undefined,
          });
          setSrp();
          return;
        }
        const selectedSRP = srps.find(({ title }) => title === value);
        setWhere({
          ...where,
          predefined: selectedSRP.refinements,
        });
        setSrp(selectedSRP);
      },
    };
  }
  if (field === 'make') {
    return {
      value: where.make || defaultWhere.make,
      options: filterValues.allMakes,
      handler: ({ target: { value } }) => setWhere({
        ...where,
        make: value === where.make ? undefined : emptyStringIsUndefined(value),
        model: undefined, // a specific make model should be cleared when clearing the make
      }),
      arrayHandler: ({ target: { value } }) => setWhere({
        ...where,
        make: toggleInArray(where.make, value),
        model: undefined, // a specific make model should be cleared when clearing the make
      }),
    };
  }
  if (field === 'model') {
    return {
      value: where.model,
      options: filterValues.modelsForSelectedMake,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'buildyear') {
    return {
      value: where.buildyear,
      options: filterValues.buildyears,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'buildyearMin') {
    return {
      value: where.buildyearMin,
      options: filterValues.buildyears,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'buildyearMax') {
    return {
      value: where.buildyearMax,
      options: filterValues.buildyears,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'body') {
    return {
      value: where.body,
      options: filterValues.bodies,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'fuel') {
    return {
      value: where.fuel,
      options: filterValues.fuels,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'transmission') {
    return {
      value: where.transmission,
      options: filterValues.transmissions,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'baseColor') {
    return {
      value: where.baseColor,
      options: filterValues.baseColors,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'color') {
    return {
      value: where.color,
      options: filterValues.colors,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'taxType') {
    return {
      value: where.taxType,
      options: filterValues.taxTypes,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'energylabel') {
    return {
      value: where.energylabel,
      options: filterValues.energylabels,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'maxTowingMin') {
    return {
      value: where.maxTowingMin,
      options: [...filterValues.maxTowings].reverse(),
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'maxTowingMax') {
    return {
      value: where.maxTowingMax,
      options: filterValues.maxTowings,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'range') {
    return {
      value: where.range,
      options: filterValues.ranges,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'rangeMin') {
    return {
      value: where.rangeMin,
      options: [...filterValues.ranges].reverse(),
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'rangeMax') {
    return {
      value: where.rangeMax,
      options: filterValues.ranges,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'electricRange') {
    return {
      value: where.electricRange,
      options: filterValues.electricRanges,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'electricRangeMin') {
    return {
      value: where.electricRangeMin,
      options: [...filterValues.electricRanges].reverse(),
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'electricRangeMax') {
    return {
      value: where.electricRangeMax,
      options: filterValues.electricRanges,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'powerHp') {
    return {
      value: where.powerHp,
      options: filterValues.powerHps,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'powerHpMin') {
    return {
      value: where.powerHpMin,
      options: [...filterValues.powerHps].reverse(),
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'powerHpMax') {
    return {
      value: where.powerHpMax,
      options: filterValues.powerHps,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'vehicleType') {
    return {
      value: where.vehicleType,
      options: filterValues.vehicleTypes,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'isNewVehicle') {
    return {
      value: where.isNewVehicle,
      options: filterValues.isNewVehicles,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'company') {
    return {
      value: where.company,
      options: filterValues.companies,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'seats') {
    return {
      value: where.seats,
      options: filterValues.seats,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'doors') {
    return {
      value: where.doors,
      options: filterValues.doors,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  if (field === 'taxAddition') {
    return {
      value: where.taxAddition,
      options: filterValues.taxAdditions,
      handler: handleKeyValueToggle,
      arrayHandler: handleKeyValueArrayToggle,
    };
  }
  return {
    value: where[field],
    handler: handleKeyValueToggle,
    arrayHandler: handleKeyValueArrayToggle,
  };
};

export default useField;
